import { Pet } from 'types/Pet';
import { Room, RoomRule, RoomRuleTypeIds, RoomRuleWeightRangeData, RoomUnit } from 'types/Room';
import { getNumericValue, getRecordValue, getFirstNumericValue } from './roomRuleParsers';
import { isPetAllowedInRoom } from './roomRestrictions';

type AssignmentRoomUnit = RoomUnit;

const getRoomUnitOccupancy = (roomUnit?: AssignmentRoomUnit): number => {
    return Math.max(getNumericValue(roomUnit?.occupancy) ?? 0, 0);
};

const getArrayRuleValues = (value: RoomRule['ruleData']) => {
    if (Array.isArray(value)) {
        return (value as Array<unknown>).filter(
            (item): item is Record<string, unknown> => !!item && typeof item === 'object'
        );
    }

    const recordValue = getRecordValue(value);

    if (!recordValue) {
        return [];
    }

    const nestedValues = Object.values(recordValue).find(Array.isArray);

    if (!Array.isArray(nestedValues)) {
        return [];
    }

    return nestedValues.filter(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object'
    );
};

const getSingleRuleRecord = (value: RoomRule['ruleData']) => {
    return getRecordValue(value) ?? getArrayRuleValues(value)[0] ?? null;
};

const getPetWeight = (pet: Pet) => {
    return getNumericValue(pet.weight);
};

export const isTotalCountRuleSatisfied = (
    rule: RoomRule,
    pets: Array<Pet>,
    roomUnit?: AssignmentRoomUnit
) => {
    const ruleData = getSingleRuleRecord(rule.ruleData);

    if (!ruleData) {
        return false;
    }

    const quantity = getFirstNumericValue(ruleData, ['quantity']);
    const existingOccupancy = getRoomUnitOccupancy(roomUnit);

    return quantity !== null && existingOccupancy + pets.length <= quantity;
};

export const isPetSizeRuleSatisfied = (
    rule: RoomRule,
    pets: Array<Pet>,
    roomUnit?: AssignmentRoomUnit
) => {
    if (roomUnit?.sizeEligibility === 'mismatch' || roomUnit?.sizeEligibility === 'inconsistent') {
        return false;
    }

    if (roomUnit && roomUnit.activeSizeRuleId !== null && roomUnit.activeSizeRuleId !== rule.id) {
        return false;
    }

    const ruleEntries = getArrayRuleValues(rule.ruleData);

    if (ruleEntries.length === 0) {
        return false;
    }

    const groupedPets = pets.reduce((result, pet) => {
        const key = `${pet.type.id}:${pet.size.id}`;

        result.set(key, (result.get(key) ?? 0) + 1);

        return result;
    }, new Map<string, number>());

    // API returns occupancy per-size for PetSize rules.
    // We can only reliably apply occupancy when all pets are the same size.
    // For mixed sizes, each size is checked independently without occupancy
    // since we don't know which size the API's occupancy refers to.
    const isSingleSizeGroup = groupedPets.size === 1;
    const existingOccupancy =
        isSingleSizeGroup && roomUnit?.activeSizeRuleId === rule.id
            ? getRoomUnitOccupancy(roomUnit)
            : 0;

    return Array.from(groupedPets.entries()).every(([groupKey, petsCount]) => {
        const [petTypeId, sizeId] = groupKey.split(':');

        const supportedQuantity = ruleEntries
            .filter(ruleEntry => ruleEntry.petTypeId === petTypeId && ruleEntry.sizeId === sizeId)
            .reduce((totalQuantity, ruleEntry) => {
                return totalQuantity + (getNumericValue(ruleEntry.quantity) ?? 0);
            }, 0);

        return supportedQuantity >= petsCount + existingOccupancy;
    });
};

export const isTotalPetWeightRuleSatisfied = (
    rule: RoomRule,
    pets: Array<Pet>,
    roomUnit?: AssignmentRoomUnit
) => {
    const ruleData = getSingleRuleRecord(rule.ruleData);

    if (!ruleData) {
        return false;
    }

    const totalSelectedPetsWeight = pets.reduce<number | null>((weightSum, pet) => {
        const petWeight = getPetWeight(pet);

        if (weightSum === null || petWeight === null) {
            return null;
        }

        return weightSum + petWeight;
    }, 0);

    if (totalSelectedPetsWeight === null) {
        return false;
    }

    const weightLimit = getFirstNumericValue(ruleData, [
        'totalWeight',
        'total_pet_weight',
        'maxWeight',
        'max_weight',
        'weight',
        'value'
    ]);

    const existingWeight = getRoomUnitOccupancy(roomUnit);

    return weightLimit !== null && existingWeight + totalSelectedPetsWeight <= weightLimit;
};

const doesPetMatchWeightRangeRule = (pet: Pet, ruleEntry: RoomRuleWeightRangeData) => {
    const petWeight = getPetWeight(pet);

    if (petWeight === null) {
        return false;
    }

    if (ruleEntry.petTypeId && ruleEntry.petTypeId !== pet.type.id) {
        return false;
    }

    if (ruleEntry.sizeId && ruleEntry.sizeId !== pet.size.id) {
        return false;
    }

    const minWeight = ruleEntry.minWeight ?? ruleEntry.min ?? ruleEntry.fromWeight ?? null;
    const maxWeight = ruleEntry.maxWeight ?? ruleEntry.max ?? ruleEntry.toWeight ?? null;

    if (minWeight !== null && petWeight < minWeight) {
        return false;
    }

    if (maxWeight !== null && petWeight > maxWeight) {
        return false;
    }

    return minWeight !== null || maxWeight !== null;
};

const canAssignPetsToWeightRanges = (
    pets: Array<Pet>,
    rangeRules: Array<RoomRuleWeightRangeData>,
    occupiedPetsCount = 0
): boolean => {
    const assignPets = (petIndex: number, remainingQuantities: Array<number>): boolean => {
        if (petIndex === pets.length) {
            return true;
        }

        const pet = pets[petIndex];
        const candidateRuleIndexes = rangeRules
            .map((ruleEntry, index) => ({ index, ruleEntry }))
            .filter(({ index, ruleEntry }) => {
                return (
                    remainingQuantities[index] > 0 && doesPetMatchWeightRangeRule(pet, ruleEntry)
                );
            })
            .map(({ index }) => index);

        for (const candidateRuleIndex of candidateRuleIndexes) {
            const nextRemainingQuantities = [...remainingQuantities];

            nextRemainingQuantities[candidateRuleIndex] -= 1;

            if (assignPets(petIndex + 1, nextRemainingQuantities)) {
                return true;
            }
        }

        return false;
    };

    const initialQuantities = rangeRules.map(ruleEntry => {
        const qty = Math.max(getNumericValue(ruleEntry.quantity) ?? 1, 0);

        if (rangeRules.length === 1) {
            return Math.max(qty - occupiedPetsCount, 0);
        }

        return qty;
    });

    return assignPets(0, initialQuantities);
};

const getWeightRangeRuleEntries = (ruleData: RoomRule['ruleData']) => {
    const arrayValues = getArrayRuleValues(ruleData);

    if (arrayValues.length > 0) {
        return arrayValues;
    }

    const recordValue = getRecordValue(ruleData);

    if (recordValue) {
        return [recordValue];
    }

    return [];
};

export const isWeightRangeRuleSatisfied = (
    rule: RoomRule,
    pets: Array<Pet>,
    roomUnit?: AssignmentRoomUnit
) => {
    const rangeRules = getWeightRangeRuleEntries(rule.ruleData)
        .map(ruleEntry => ({
            ...ruleEntry,
            quantity: getNumericValue(ruleEntry.quantity) ?? 1,
            minWeight: getFirstNumericValue(ruleEntry, [
                'minWeight',
                'min_weight',
                'min',
                'fromWeight',
                'from_weight',
                'weightFrom'
            ]),
            maxWeight: getFirstNumericValue(ruleEntry, [
                'maxWeight',
                'max_weight',
                'max',
                'toWeight',
                'to_weight',
                'weightTo'
            ])
        }))
        .filter(ruleEntry => ruleEntry.minWeight !== null || ruleEntry.maxWeight !== null);

    if (rangeRules.length === 0) {
        return false;
    }

    return canAssignPetsToWeightRanges(pets, rangeRules, getRoomUnitOccupancy(roomUnit));
};

export const isRoomRuleSatisfied = (
    rule: RoomRule,
    pets: Array<Pet>,
    roomUnit?: AssignmentRoomUnit
) => {
    switch (rule.ruleTypeId) {
        case RoomRuleTypeIds.TotalCount:
            return isTotalCountRuleSatisfied(rule, pets, roomUnit);
        case RoomRuleTypeIds.PetSize:
            return isPetSizeRuleSatisfied(rule, pets, roomUnit);
        case RoomRuleTypeIds.TotalPetWeight:
            return isTotalPetWeightRuleSatisfied(rule, pets, roomUnit);
        case RoomRuleTypeIds.WeightRange:
            return isWeightRangeRuleSatisfied(rule, pets, roomUnit);
        default:
            return false;
    }
};

export const doesRoomUnitAllowPets = (
    room: Room,
    roomUnit: AssignmentRoomUnit,
    pets: Array<Pet>
) => {
    if (!pets.every(pet => isPetAllowedInRoom(room, pet))) {
        return false;
    }

    const petSizeRules = room.room_rules.filter(
        rule => rule.ruleTypeId === RoomRuleTypeIds.PetSize
    );

    const otherRules = room.room_rules.filter(rule => rule.ruleTypeId !== RoomRuleTypeIds.PetSize);

    return (
        otherRules.every(rule => isRoomRuleSatisfied(rule, pets, roomUnit)) &&
        (petSizeRules.length === 0 ||
            petSizeRules.some(rule => isRoomRuleSatisfied(rule, pets, roomUnit)))
    );
};
